import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMinus, faPlus, faXmark } from '@fortawesome/sharp-solid-svg-icons';
import { AnimatePresence, motion } from 'framer-motion';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import Draggable from 'react-draggable';
import { twMerge } from 'tailwind-merge';

import { ButtonSize, ButtonVariant } from '@/components/button';
import IconButton from '@/components/button/iconButton';
import Tabs from '@/components/tabs';
import Typography from '@/components/typography';
import { useNavigationContext } from '@/features/navigation/provider';

import { customizeIconMap, PointIds } from '../config';
import { CustomizePoint } from '../customizePoint';
import { CustomizeFeature, useCustomizeLandContext } from '../provider';
import CustomizeSettingsDetails from './customizeSettingsDetails';
import CustomizeSettingsFormat from './customizeSettingsFormat';
import CustomizeSettingsMedia from './customizeSettingsMedia';

interface IProps {
  className?: string;
}

const tabs = [
  { label: 'Details', value: 'details' },
  { label: 'Format', value: 'format' },
  { label: 'Media', value: 'media' },
];
export interface ICustomizeTabProps {
  activeFeature: CustomizeFeature;
}
const TabPages = {
  details: {
    content: <CustomizeSettingsDetails />,
  },
  format: {
    content: <CustomizeSettingsFormat />,
  },
  media: {
    content: <CustomizeSettingsMedia />,
  },
};

const CustomizeSettingsBox = ({ className = '' }: IProps) => {
  const [isOpen, setIsOpen] = useState(true);
  const [activeTab, setActiveTab] = useState(tabs[0].value);
  const { activeFeature, setActiveFeature } = useCustomizeLandContext();
  const [dragging, setDragging] = useState(false);
  const draggableRef = useRef(null);

  const { aerialMapIsOpen } = useNavigationContext();

  const activeFeatureId = useMemo(() => activeFeature?.id, [activeFeature]);

  useEffect(() => {
    setActiveTab(tabs[0].value);
  }, [activeFeatureId]);

  const isPoint = useMemo(() => {
    if (!activeFeature?.properties?.id) return false;
    return Object.values(PointIds).includes(
      activeFeature?.properties.id as PointIds
    );
  }, [activeFeature?.properties?.id]);

  if (!activeFeature) return null;

  return (
    <Draggable
      bounds="parent"
      cancel="textarea"
      defaultClassName="top-[62px] right-0" // Default to be below the search bar
      enableUserSelectHack={false} // This allows you to select text https://github.com/react-grid-layout/react-draggable/blob/2c3aa84352ca8eaed42a9ac0b59a1187e7850b6a/lib/DraggableCore.js#L97
      grid={[14, 14]}
      nodeRef={draggableRef}
      onDrag={() => {
        if (!dragging) {
          setDragging(true);
        }
      }}
    >
      <div
        ref={draggableRef}
        className={twMerge(
          'visible flex w-[280px] shrink flex-col bg-white shadow',
          aerialMapIsOpen ? 'hidden' : '',
          className
        )}
      >
        <span
          className="flex items-center justify-between p-2"
          role="button"
          tabIndex={0}
          onClick={() => {
            if (dragging) {
              setDragging(false);
            } else {
              setIsOpen((prev) => !prev);
            }
          }}
          onKeyDown={(e) =>
            (e.code === 'Space' || e.code === 'Enter') &&
            setIsOpen((prev) => !prev)
          }
        >
          <div className="flex items-center gap-2 overflow-hidden truncate ">
            <div className="flex w-6 items-center justify-center">
              {isPoint ? (
                <CustomizePoint
                  isReadOnly
                  activeFeature={null}
                  map={undefined}
                  point={activeFeature}
                />
              ) : (
                customizeIconMap[activeFeature?.properties?.id] ?? null
              )}
            </div>
            <Typography
              className="overflow-hidden truncate text-ellipsis text-gray-70"
              variant="h5"
            >
              {activeFeature?.properties?.name}
            </Typography>
          </div>

          <span className="flex gap-3">
            <IconButton size={ButtonSize.XS} variant={ButtonVariant.GHOST}>
              <FontAwesomeIcon icon={isOpen ? faMinus : faPlus} size="lg" />
            </IconButton>
            <IconButton
              size={ButtonSize.XS}
              variant={ButtonVariant.GHOST}
              onClick={(e) => {
                e.stopPropagation();
                setActiveFeature(null);
              }}
            >
              <FontAwesomeIcon icon={faXmark} size="xl" />
            </IconButton>
          </span>
        </span>
        <AnimatePresence initial>
          {isOpen && (
            <motion.section
              key="content"
              animate="open"
              exit="collapsed"
              initial="collapsed"
              transition={{ duration: 0.2, ease: [0.04, 0.62, 0.23, 0.98] }}
              variants={{
                collapsed: { height: 0, opacity: 0 },
                open: { height: 'auto', opacity: 1 },
              }}
            >
              <div className="flex flex-col gap-2 bg-white px-2 py-3">
                <Tabs
                  activeValue={activeTab}
                  tabs={tabs}
                  onChange={(t) => setActiveTab(t)}
                />
                {TabPages[activeTab].content}
              </div>
            </motion.section>
          )}
        </AnimatePresence>
      </div>
    </Draggable>
  );
};

export default CustomizeSettingsBox;
